// Defensive renderer for backend error messages.
//
// Why: FastAPI/pydantic 422 responses have `detail` as an ARRAY of
// `{type, loc, msg, input, url}` objects — not a string. If a frontend
// component does `setError(err.response.data.detail)` and then renders
// `{error}` in JSX, React explodes with:
//
//   Objects are not valid as a React child (found: object with keys
//   {type, loc, msg, input, url}). If you meant to render a collection
//   of children, use an array instead.
//
// (See the 2026-05-30 islands-bbox repro: `min_dom=NaN` slipped into a
// query, 422'd, the detail array landed in `setError`, React crashed.)
//
// This helper normalizes anything axios might hand us into a flat
// human-readable string, so callers can blindly:
//   setError(errorMessage(err, 'Failed to do thing'))
//
// Precedence (highest → lowest):
//   1. response.data.detail (string / array of pydantic items / single dict)
//      — actual backend explanation; always wins
//   2. response.data.message — backend-supplied human message
//   3. caller-provided fallback — page-specific copy like
//      "Failed to mark all alerts as read"; preferred over a generic
//      JS Error.message ("Network Error", "Request failed with status
//      code 500", "boom") which leaks transport-layer noise into the UI
//   4. err.message — last-resort for non-axios errors when the caller
//      didn't supply a fallback
//   5. "Unexpected error" — final safety net
//
// Accepted error shapes:
//   - axios error with response.data.detail as string  → that string
//   - axios error with response.data.detail as array of
//     pydantic items → "field: msg; field: msg"  (max 3, then "...")
//   - axios error with response.data.detail as a single item dict
//     ({type,loc,msg,...}) → "field: msg"
//   - axios error with no detail but response.data.message → that
//   - bare Error / non-axios error with fallback → fallback
//   - bare Error / non-axios error without fallback → err.message
//   - bare string thrown directly → that string
//   - undefined / null → fallback or "Unexpected error"

function _pydanticItemToString(item) {
  if (!item || typeof item !== 'object') return String(item ?? '');
  // pydantic v2: loc is an array like ["query", "min_dom"]; msg is the
  // human message; type is the validator name.
  const loc = Array.isArray(item.loc) ? item.loc.slice(1).join('.') || item.loc[0] : item.loc;
  const msg = item.msg || item.message || item.type || JSON.stringify(item);
  return loc ? `${loc}: ${msg}` : String(msg);
}

export function errorMessage(err, fallback) {
  if (!err) return fallback || 'Unexpected error';

  // Bare string thrown directly
  if (typeof err === 'string') return err;

  // axios error shape — backend-supplied detail/message always wins
  const data = err.response?.data;
  const detail = data?.detail;

  if (Array.isArray(detail)) {
    const parts = detail.slice(0, 3).map(_pydanticItemToString);
    if (detail.length > 3) parts.push(`(+${detail.length - 3} more)`);
    return parts.join('; ');
  }
  if (typeof detail === 'string' && detail.length > 0) return detail;
  if (detail && typeof detail === 'object') return _pydanticItemToString(detail);

  if (typeof data?.message === 'string' && data.message.length > 0) {
    return data.message;
  }

  // No backend message available. Prefer the caller's page-specific
  // fallback over generic JS Error.message (e.g. "Network Error",
  // "Request failed with status code 500", or a test's mocked "boom").
  if (fallback) return fallback;

  if (typeof err.message === 'string' && err.message.length > 0) {
    return err.message;
  }

  return 'Unexpected error';
}

export default errorMessage;
